<template>
  <div class="scenario-navigator">
    <!-- Topic Navigation -->
    <div class="has-addons mr-2">
      <button-tooltip
        tooltip="Previous Topic ( [ )"
        tooltip-type="is-info is-light"
        icon-left="chevron-left"
        @click="$emit('navigateTopic', -1)"
      />

      <button-tooltip
        class="fixed-width-counter"
        :tooltip="`🔎 Click to select from ${topicCount} topics ( \\ )`"
        :label="`${topicIndex + 1} / ${topicCount}`"
        type="is-info is-light"
        outlined
        @click="$emit('openTopicSelector')"
      />

      <button-tooltip
        tooltip="Next Topic ( ] )"
        tooltip-type="is-info is-light"
        icon-left="chevron-right"
        @click="$emit('navigateTopic', 1)"
      />
    </div>

    <!-- Example Navigation -->
    <div class="has-addons mr-2">
      <button-tooltip
        tooltip="Previous Example ( ; )"
        tooltip-type="is-info is-light"
        icon-left="chevron-left"
        @click="$emit('navigateScenario', -1)"
      />

      <button-tooltip
        class="fixed-width-counter"
        :tooltip="`🔎 Click to preview ${scenarioCount} examples ( ⏎ )`"
        :label="`${scenarioIndex + 1} / ${scenarioCount}`"
        type="is-info is-light"
        outlined
        @click="$emit('previewScenarios')"
      />

      <button-tooltip
        tooltip="Next Example ( ' )"
        tooltip-type="is-info is-light"
        icon-left="chevron-right"
        @click="$emit('navigateScenario', 1)"
      />

      <button-tooltip
        tooltip="Edit current example (E)"
        icon-left="edit"
        type="is-warning is-light"
        outlined
        @click="$emit('editScenario')"
      />
    </div>

    <!-- Action Buttons -->
    <button-tooltip
      class="mr-2"
      tooltip="Custom FEN (C)"
      type="is-info is-light"
      icon-left="chess-board"
      outlined
      @click="$emit('customFen')"
    />

    <button-tooltip
      v-if="!isPresentationMode"
      class="mr-2"
      :tooltip="showMovesTable ? 'Hide moves (M)' : 'Show moves (M)'"
      :type="`${showMovesTable ? 'is-light is-success' : 'is-light is-dark'}`"
      :icon-left="showMovesTable ? 'eye' : 'eye-slash'"
      outlined
      @click="$emit('toggleMovesTable')"
    />

    <button-tooltip
      class="mr-2"
      tooltip="Start quiz (T)"
      label="Quiz"
      type="is-primary is-light"
      icon-left="puzzle-piece"
      :disabled="!isQuizAvailable"
      outlined
      @click="$emit('startQuiz')"
    />

    <button-tooltip
      :tooltip="
        isPresentationMode
          ? 'Exit presentation mode (F11)'
          : 'Open presentation mode in new window (F11)'
      "
      :label="isPresentationMode ? 'Exit' : 'Present'"
      :type="isPresentationMode ? 'is-danger is-light' : 'is-info is-light'"
      :icon-left="isPresentationMode ? 'compress' : 'desktop'"
      outlined
      @click="$emit('togglePresentation')"
    />
  </div>
</template>

<script>
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
export default {
  components: { ButtonTooltip },
  name: "ScenarioNavigator",

  props: {
    topicIndex: {
      type: Number,
      required: true,
    },
    topicCount: {
      type: Number,
      required: true,
    },
    scenarioIndex: {
      type: Number,
      required: true,
    },
    scenarioCount: {
      type: Number,
      required: true,
    },
    isQuizAvailable: {
      type: Boolean,
      required: true,
    },
    showMovesTable: {
      type: Boolean,
      default: true,
    },
    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    handleKeyDown(event) {
      // Check if user is typing in an input field
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      // Skip if any modifier key is pressed (Ctrl, Alt, Meta/Command)
      if (event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }

      // Get the active modal if any
      const activeModal = document.querySelector(".modal.is-active");
      const isTopicSelectorOpen = activeModal?.querySelector(".topic-selector");

      // If topic selector is open, only handle Escape key
      if (isTopicSelectorOpen) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.$emit("closeTopicSelector");
        }
        return;
      }

      // If any other modal is open, don't handle navigation keys
      if (activeModal) return;

      switch (event.key) {
        case "[":
          event.preventDefault();
          this.$emit("navigateTopic", -1);
          break;
        case "]":
          event.preventDefault();
          this.$emit("navigateTopic", 1);
          break;
        case "\\":
          event.preventDefault();
          this.$emit("openTopicSelector");
          break;
        case ";":
          event.preventDefault();
          this.$emit("navigateScenario", -1);
          break;
        case "'":
          event.preventDefault();
          this.$emit("navigateScenario", 1);
          break;
        case "Enter":
          event.preventDefault();
          this.$emit("previewScenarios");
          break;
        case "e":
          event.preventDefault();
          this.$emit("editScenario");
          break;
        case "c":
          event.preventDefault();
          this.$emit("customFen");
          break;
        case "m":
          event.preventDefault();
          this.$emit("toggleMovesTable");
          break;
        case "t":
          if (this.isQuizAvailable) {
            event.preventDefault();
            this.$emit("startQuiz");
          }
          break;
        case "F11":
          event.preventDefault();
          this.$emit("togglePresentation");
          break;
      }
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyDown);
  },
};
</script>

<style scoped>
.scenario-navigator {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #eee;
}

.fixed-width-counter {
  width: 5.5rem;
}

.fixed-width-counter::v-deep .button {
  width: 100%;
  justify-content: center;
}
</style>
